import {
    CLEAR_STATE,
    DELETE_LEG,
    DELETE_LEG_SAGA,
    ERASE_LEGS_SNAPSHOT,
    FETCH_ACTIVITY_DATA,
    FETCH_ACTIVITY_DATA_FAIL,
    FETCH_ACTIVITY_DATA_START,
    FETCH_ACTIVITY_DATA_SUCCESS,
    FETCH_ACTIVITY_DATA_SUCCESS_V1,
    FETCH_SINGLE_SPORT_ACTIVITY,
    FETCH_ACTIVITY_DATA_USING_V1_ENDPOINT,
    FETCH_ALL_CHILDS_METRICS_DATA_SUCCESS,
    FETCH_CHILD_ACTIVITY_DATA_FAIL,
    FETCH_CHILD_ACTIVITY_DATA_SUCCESS,
    HIDE_ALERT,
    INSERT_LEG,
    MAKE_LEGS_SNAPSHOT,
    MAKE_TRIM_SNAPSHOT,
    PUT_RECONSTRUCTED_ACTIVITY_DATA,
    PUT_RECONSTRUCTED_ACTIVITY_DATA_FAIL,
    PUT_RECONSTRUCTED_ACTIVITY_DATA_START,
    PUT_RECONSTRUCTED_ACTIVITY_DATA_SUCCESS,
    RESET_TRIM,
    RESTORE_LEGS_SNAPSHOT,
    RESTORE_TRIM_SNAPSHOT,
    SET_ACTIVE_LEG,
    SET_CURRENT_LEG_INDEX,
    SET_START_TO_TIMESTAMP,
    SET_TRIM_END,
    SET_TRIM_START,
    UPDATE_ACTIVITY_TYPE,
    FETCH_ALL_CHILDS_METRICS_DATA_START,
    FETCH_ALL_CHILDS_METRICS_DATA_FAIL
} from './ActionTypes';
import { selectReconstructDTOs } from 'reducers/multiSportEdit/multiSportSelectors';

/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityReconstructRequestDTO').ActivityReconstructRequestDTO} ActivityReconstructRequestDTO */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityMetricsDTO').ActivityMetricsDTO} ActivityMetricsDTO */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityMetricsDTO_v2').ActivityMetricsDTO_v2} ActivityMetricsDTO_v2 */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityDataDTO').ActivityDataDTO} ActivityDataDTO */
/** @typedef {import('reducers/multiSportEdit/types/entity/MultiSportState').TrimEventType} TrimEventType */
/** @typedef {import('reducers/multiSportEdit/types/entity/MultiSportState').MultiSportState} MultiSportState */
/** @typedef {import('reducers/multiSportEdit/types/entity/LegMetrics').LegMetrics} LegMetrics */
/** @typedef {import('reducers/multiSportEdit/types/entity/MetricsByKey').MetricsByKey} MetricsByKey */
/** @typedef {import('reducers/multiSportEdit/types/entity/LegIndexData').LegIndexData} LegIndexData */

/**
 * sagas
 */

/**
 * @param {{
 *  activityId: string;
 * }} payload
 */
export function fetchActivityData(payload) {
    return {
        type: FETCH_ACTIVITY_DATA,
        payload
    };
}

/**
 * @param {{
 *  activityId: string;
 * }} payload
 */
export function fetchActivityDataUsingV1Endpoint(payload) {
    return {
        type: FETCH_ACTIVITY_DATA_USING_V1_ENDPOINT,
        payload
    };
}

/** @param {number} legIndex */
export const setActiveLeg = (legIndex) => ({
    type: SET_ACTIVE_LEG,
    payload: { legIndex }
});

/**
 * @param {{
 *  selectReconstructDTOs: (state: MultiSportState) => ReturnType<typeof selectReconstructDTOs>;
 *  navigationParams?: string;
 *  isConvertOperation?: boolean;
 * }} payload
 * @returns
 */
export const putReconstructedActivityData = (payload) => ({
    type: PUT_RECONSTRUCTED_ACTIVITY_DATA,
    payload
});

/**
 * Fetches a single-sport activity into rootState.multiSportActivitySummary
 *
 * @param {{
 *  activityId: string;
 * }} payload
 */
export function fetchSingleSportActivity(payload) {
    return {
        type: FETCH_SINGLE_SPORT_ACTIVITY,
        payload
    };
}

/**
 * store action handlers
 */

/**
 * @param {{
 *  response: {status: number};
 * }} error
 */
export function fetchActivityDataFail(error) {
    return {
        type: FETCH_ACTIVITY_DATA_FAIL,
        payload: error
    };
}

export const putReconstructedActivityDataStart = () => ({
    type: PUT_RECONSTRUCTED_ACTIVITY_DATA_START
});

export const putReconstructedActivityDataSuccess = () => ({
    type: PUT_RECONSTRUCTED_ACTIVITY_DATA_SUCCESS
});

export const putReconstructedActivityDataFail = () => ({
    type: PUT_RECONSTRUCTED_ACTIVITY_DATA_FAIL
});

export const fetchActivityDataStart = () => ({
    type: FETCH_ACTIVITY_DATA_START
});

/**
 * @param {{
 *   activityData: ActivityDataDTO;
 * }} payload
 * @returns
 */
export const fetchActivityDataSuccess = (payload) => ({
    type: FETCH_ACTIVITY_DATA_SUCCESS,
    payload
});

/**
 * @param {{
 *  childActivities: ActivityDataDTO[];
 * }} payload
 */
export const fetchChildActivityDataSuccess = (payload) => ({
    type: FETCH_CHILD_ACTIVITY_DATA_SUCCESS,
    payload
});

export const fetchChildActivityDataFail = () => ({
    type: FETCH_CHILD_ACTIVITY_DATA_FAIL
});

/**
 * @param {number} legIndex
 */
export const setCurrentLegIndex = (legIndex) => ({
    type: SET_CURRENT_LEG_INDEX,
    payload: { currentLegIndex: legIndex }
});

/**
 * @param {{
 *   legIndex: number; // the leg whose start to set
 *   timestamp: number; // the new start timestamp for the given leg
 * }} payload
 */
export const setStartToTimestamp = (payload) => ({
    type: SET_START_TO_TIMESTAMP,
    payload
});

/**
 * @param {{
 *  metricsByKey: MetricsByKey;
 *  legIndexData: LegIndexData[];
 * }} payload
 */
export const fetchActivityDataSuccessV1 = (payload) => ({
    type: FETCH_ACTIVITY_DATA_SUCCESS_V1,
    payload
});

export const clearState = () => ({
    type: CLEAR_STATE
});

/**
 * @param {{
 *  timestamp: number
 *  typeKey: string;
 *  typeId: number;
 *  insertAtIndex: number
 * }} payload
 */
export const insertLeg = (payload) => ({
    type: INSERT_LEG,
    payload
});

/** @typedef {MERGE_STRATEGY_WITH_PREVIOUS_LEG | MERGE_STRATEGY_WITH_NEXT_LEG} MergeStrategy */
export const MERGE_STRATEGY_WITH_PREVIOUS_LEG = 'MERGE_STRATEGY_WITH_PREVIOUS_LEG';
export const MERGE_STRATEGY_WITH_NEXT_LEG = 'MERGE_STRATEGY_WITH_NEXT_LEG';

/**
 * @param {{
 *  legIndex: number;
 *  mergeStrategy?: MergeStrategy;
 * }} payload
 */
export const deleteLeg = (payload) => ({
    type: DELETE_LEG,
    payload
});

/**
 * @param {{
 *  legIndex: number;
 *  mergeStrategy?: MergeStrategy;
 * }} payload
 */
export const deleteLegSaga = (payload) => ({
    type: DELETE_LEG_SAGA,
    payload
});

/**
 * @param {{
 *  activityMetricsDTO: ActivityMetricsDTO & ActivityMetricsDTO_v2;
 * }} payload
 */
export const fetchAllChildsMetricsDataSuccess = (payload) => ({
    type: FETCH_ALL_CHILDS_METRICS_DATA_SUCCESS,
    payload
});

/**
 * @param {string} snapshotName
 */
export const makeLegsSnapshot = (snapshotName) => ({
    type: MAKE_LEGS_SNAPSHOT,
    payload: { snapshotName }
});

/**
 * @param {string} snapshotName
 */
export const restoreLegsSnapshot = (snapshotName) => ({
    type: RESTORE_LEGS_SNAPSHOT,
    payload: { snapshotName }
});

/**
 * @param {string} snapshotName
 */
export const eraseLegsSnapshot = (snapshotName) => ({
    type: ERASE_LEGS_SNAPSHOT,
    payload: { snapshotName }
});

export const hideAlert = () => ({
    type: HIDE_ALERT
});

/** @param {{ timestamp: number; eventType?: TrimEventType }} param0 */
export const setTrimStart = ({ timestamp, eventType = 'ON_CHANGING' }) => ({
    type: SET_TRIM_START,
    payload: { timestamp, eventType }
});

/** @param {{ timestamp: number; eventType?: TrimEventType }} param0 */
export const setTrimEnd = ({ timestamp, eventType = 'ON_CHANGING' }) => ({
    type: SET_TRIM_END,
    payload: { timestamp, eventType }
});

export const makeTrimSnapshot = () => ({
    type: MAKE_TRIM_SNAPSHOT
});

export const restoreTrimSnapshot = () => ({
    type: RESTORE_TRIM_SNAPSHOT
});

export const resetTrim = () => ({
    type: RESET_TRIM
});

/**
 * @param {{
 *  activityId: number;
 *  typeKey: string;
 *  typeId: number;
 * }} payload
 */
export const updateActivityType = (payload) => ({
    type: UPDATE_ACTIVITY_TYPE,
    payload
});
